// components/list/FactoryReferenceResults.tsx
'use client';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { OpenAIFolderIcon } from '@/components/ui/OpenAIFolderIcon';
import { FactoryImage } from '@/components/marketing/FactoryImage';
import { URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import { buildAssetRenditionUrl } from '#lib/asset-rendition';
import { factoryImageId, factoryImageSelectionSchema } from '#lib/list/factoryImageMentions';
import { isAllowedFactoryReferenceUrl } from '#lib/list/factoryReferenceSecurity';
import { chooseFactoryReference, factoryLoadContext, loadFactoryChatBank } from '#lib/list/factoryNavigation';
import type { OrganisationRuntimeState, SessionRuntimeState } from '#types/types';

const foldersSchema = z.array(z.object({ id: z.string().min(1).max(120) })).max(12);
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#444444]';

export function FactoryReferenceResults({ result, kind, sessionId }: { result: unknown; kind: string; sessionId: string }) {
  const { kernel } = useKernel();
  const context = useKernelStore(state => factoryLoadContext(state.userContext));
  const factory = useKernelStore(state => state.inventory.Organisation.registry[URW_ORGANISATION_ID]?.factory);
  const studioSection = useKernelStore(state => state.studioSection);
  const load = useKernelStore(state => (state.entityKernelState?.[`Organisation:${URW_ORGANISATION_ID}`]?.state?.runtime as OrganisationRuntimeState | undefined)?.factoryLoad);
  const status = load?.context === context ? load.status : 'loading';
  const cards = useRef<HTMLElement>(null);
  const focusImage = useKernelStore(state => (state.entityKernelState?.[`ChatSession:${sessionId}`]?.state?.runtime as SessionRuntimeState | undefined)?.factoryReferenceFocus);
  useEffect(() => {
    if (!kernel.current) return;
    void loadFactoryChatBank(kernel.current).catch(() => undefined);
  }, [context, sessionId]);
  useEffect(() => {
    if (studioSection !== 'list' || status !== 'ready' || !focusImage) return;
    const target = Array.from(cards.current?.querySelectorAll<HTMLButtonElement>('[data-factory-image-link]') ?? []).find(element => element.dataset.factoryImageLink === focusImage);
    if (target && !target.disabled) {
      const frame = requestAnimationFrame(() => {
        target.focus();
        if (document.activeElement === target) kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { factoryReferenceFocus: undefined });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [studioSection, status, focusImage]);
  const body = result && typeof result === 'object' ? result as Record<string, unknown> : {};
  const shown = factoryImageSelectionSchema.safeParse(body.images);
  const discovered = foldersSchema.safeParse(body.folders);
  const bank = status === 'ready' && factory?.status === 'ready' ? factory.referenceFolders : [];
  const choose = (folderId: string, imageId?: string, action?: 'open' | 'reference') => {
    const k = kernel.current;
    if (!k) return;
    void chooseFactoryReference(k, sessionId, folderId, imageId, action).catch(error => k.handleError(error));
  };
  if (kind === 'LIST_FACTORY_FOLDERS' && discovered.success) return <section aria-label="Factory folders" className="min-w-0 -mx-3">
    {!discovered.data.length && <p className="px-3 text-[#AFAFAF]" style={{ fontSize: 14 }}>No matching Factory folders.</p>}
    {discovered.data.map(item => {
      const folder = bank.find(folder => folder.id === item.id);
      return <button key={item.id} type="button" className={`flex w-full max-w-full items-center gap-3 rounded-[12px] px-3 py-2 text-left text-[#ECECEC] transition-colors duration-150 hover:bg-white/[0.08] disabled:text-[#8E8E8E] disabled:hover:bg-transparent ${focusRing}`} disabled={!folder} onClick={() => choose(item.id)}>
        <OpenAIFolderIcon className="h-4 w-4 shrink-0 text-[#B4B4B4]" />
        <span className="min-w-0 truncate" style={{ fontSize: 14 }}>{folder ? folder.name : status === 'loading' ? 'Loading folder' : 'Folder unavailable'}</span>
        {folder && <span className="ml-auto shrink-0 text-[#8E8E8E]" style={{ fontSize: 13 }}>{folder.images.length} images</span>}
      </button>;
    })}
  </section>;
  if (kind !== 'SHOW_FACTORY_REFERENCES' || !shown.success) return null;
  return <section ref={cards} aria-label="Factory image references" className="min-w-0 w-full">
    {!shown.data.length && <p className="text-[#AFAFAF]" style={{ fontSize: 14 }}>No images selected.</p>}
    <div className="grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2">
      {shown.data.map((identity, index) => {
        const folder = bank.find(folder => folder.id === identity.folderId);
        const image = folder?.images.find(image => image.id === identity.imageId);
        const name = image?.name ?? (status === 'loading' ? 'Loading image' : 'Image unavailable');
        const thumbnail = image && (buildAssetRenditionUrl(image.url, 320) || (image.thumbnailUrl && isAllowedFactoryReferenceUrl(image.thumbnailUrl) ? image.thumbnailUrl : undefined));
        return <article key={factoryImageId(identity)} className="group min-w-0">
          <div className="relative overflow-hidden rounded-[16px] bg-[#1B1B1B]">
            <button type="button" data-factory-image-link={factoryImageId(identity)} aria-label={`Open image ${index + 1}: ${name}`} className={`block w-full rounded-[16px] ${focusRing}`} disabled={!image} onClick={() => choose(identity.folderId, identity.imageId)}>
              <FactoryImage src={thumbnail || undefined} alt={name} className="aspect-square w-full" />
            </button>
            <button type="button" disabled={!image} title="Reference this image" aria-label={`Reference image ${index + 1}: ${name}`}
              className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#303030] text-[#ECECEC] opacity-0 transition-opacity duration-150 hover:bg-[#3A3A3A] focus-visible:opacity-100 group-hover:opacity-100 disabled:hidden [@media(hover:none)]:opacity-100 ${focusRing}`}
              onClick={() => choose(identity.folderId, identity.imageId, 'reference')}>
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>@</span>
            </button>
          </div>
          <p className="mt-2 truncate text-[#ECECEC]" style={{ fontSize: 14 }} title={name}>{name}</p>
          <button type="button" className={`block max-w-full truncate rounded text-left text-[#8E8E8E] transition-colors duration-150 hover:text-[#ECECEC] disabled:hover:text-[#8E8E8E] ${focusRing}`} style={{ fontSize: 13 }} disabled={!folder} onClick={() => choose(identity.folderId)}>
            {folder?.name ?? 'Factory'}
          </button>
        </article>;
      })}
    </div>
  </section>;
}
